/**
 * Accent-insensitive matching for the search palette, mirroring the server's `f_unaccent(lower(...))`: "gedeon"
 * finds and highlights "Gédéon", and every word of the query must match.
 */

export type HighlightSegment = { text: string; match: boolean }

const COMBINING_MARKS = /\p{M}/gu

/** Lowercase and strip diacritics (NFD, then drop combining marks). */
export function foldForSearch(value: string): string {
  return value.normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase()
}

/** The query's words, folded; empty words are dropped. */
export function searchWords(query: string): string[] {
  return foldForSearch(query)
    .split(/\s+/)
    .filter((word) => word.length > 0)
}

/** True when every word of `query` appears in at least one of `values` (accent and case insensitive). */
export function matchesAllWords(query: string, ...values: Array<string | null | undefined>): boolean {
  const words = searchWords(query)
  if (words.length === 0) return true
  const haystack = foldForSearch(values.filter(Boolean).join(" "))
  return words.every((word) => haystack.includes(word))
}

/**
 * Splits `text` into matched / unmatched segments for every word of `query`. Matching happens on the folded
 * text, but the segments keep the original characters (accents included).
 */
export function highlightMatch(text: string, query: string): HighlightSegment[] {
  const words = searchWords(query)
  if (!text) return []
  if (words.length === 0) return [{ text, match: false }]

  // Fold character by character, remembering which original character each folded character came from, so a
  // match found in the folded string maps back to the original one even when folding changed lengths.
  const chars = Array.from(text)
  let folded = ""
  const origin: number[] = []
  chars.forEach((char, index) => {
    const foldedChar = foldForSearch(char)
    folded += foldedChar
    for (let i = 0; i < foldedChar.length; i += 1) origin.push(index)
  })

  const marked = new Array<boolean>(chars.length).fill(false)
  for (const word of words) {
    let from = folded.indexOf(word)
    while (from !== -1) {
      for (let i = from; i < from + word.length; i += 1) marked[origin[i]!] = true
      from = folded.indexOf(word, from + word.length)
    }
  }

  const segments: HighlightSegment[] = []
  chars.forEach((char, index) => {
    const match = marked[index]!
    const last = segments[segments.length - 1]
    if (last && last.match === match) last.text += char
    else segments.push({ text: char, match })
  })
  return segments
}
